import * as Sentry from "@sentry/react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { config, isDev } from "./configuration";
import { CORRELATION_ID_HEADER, CORRELATION_ID_TAG, getErrorCorrelationId } from "./correlation-id";

/**
 * The API is cross-origin (api.<zone> vs app.<zone>), so trace headers are only sent to its exact origin. The API must
 * allow `sentry-trace` and `baggage` in CORS before this ships, otherwise every preflight fails.
 */
function getApiTracePropagationTargets(): RegExp[] {
  try {
    const apiOrigin = new URL(config.apiEndpoint).origin;
    const escapedApiOrigin = apiOrigin.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    return [new RegExp(`^${escapedApiOrigin}(/|$)`)];
  } catch {
    return [];
  }
}

Sentry.init({
  dsn: config.sentryDsn,
  integrations: [
    Sentry.replayIntegration(),
    Sentry.browserTracingIntegration({
      onRequestSpanStart(span, { headers }) {
        const correlationId = headers?.get(CORRELATION_ID_HEADER);
        if (correlationId) {
          span.setAttribute(CORRELATION_ID_TAG, correlationId);
        }
      },
    }),
  ],

  // `http.client` spans only exist while a pageload or navigation span is active. Only those requests pass this
  // sampling decision on; for later requests (most clicks) API and Lambdas sample with their own rate. Errors are
  // captured regardless, and the correlation ID finds unsampled requests in Loki.
  tracesSampleRate: 0.2,
  tracePropagationTargets: getApiTracePropagationTargets(),

  // Capture Replay for 10% of all sessions,
  // plus for 100% of sessions with an error
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  dataCollection: {
    userInfo: false,
    cookies: false,
    httpHeaders: {
      request: { deny: ["forwarded", "-ip", "remote-", "via", "-user"] },
      response: { deny: ["forwarded", "-ip", "remote-", "via", "-user"] },
    },
    httpBodies: [],
    urlQueryParams: { deny: ["forwarded", "-ip", "remote-", "via", "-user"] },
    genAI: { inputs: false, outputs: false },
    databaseQueryData: false,
    queues: false,
    graphQL: { document: false, variables: false },
  },
  environment: config.appEnvironment,
  release: config.version,
  enabled: !isDev,
  beforeSend(event, hint) {
    const error = hint.originalException;

    if (error instanceof InteractionRequiredAuthError) {
      return null;
    }

    const correlationId = getErrorCorrelationId(error);
    if (correlationId) {
      event.tags = { ...event.tags, [CORRELATION_ID_TAG]: correlationId };
    }

    return event;
  },
});

export default Sentry;
